import React, { FunctionComponent, useState } from "react";
import { connect, MapDispatchToProps, MapStateToProps, MergeProps } from "react-redux";

import { Tab, Tabs } from "@multiweb/reactComponents";
import { getPrimaryText } from "@multiweb/reactCore/i18n";
import { IAppState } from "@multiweb/types/react";
import { createUpdateActivityAction } from "../../../actions";
import { ActivityEditDialogTabsIndex } from "../../../enums";
import { LOC_ACTIONS, LOC_PARTICIPANTS, LOC_PROPERTIES, LOC_TRANSITION_FROM_TO, LOC_TRIGGERS } from "../../../locKeys";
import { getActivityByIndex, isEditDialogHidden } from "../../../selectors";
import { IAction, IActivity, IParticipant, ITrigger, WorkflowEditorProps } from "../../../types";
import { UiActivity } from "../../../UiActivity";
import { UiWorkflow } from "../../../UiWorkflow";
import { getUiWorkflowMutableObject } from "../../../utils";
import { EditDialog } from "../EditDialog";
import { ActionsTab, ActivityPropertiesTab, ParticipantsTab, TransitionsFromToTab, TriggersTab } from "./tabs";
import { createNewActions, createNewParticipants, createNewTriggers } from "./utils";

type ActivityEditDialogComponentProps = {
    readonly activity: IActivity;
    readonly isHidden: boolean;
    readonly uiWorkflow: UiWorkflow;
    readonly defaultTabIndex?: ActivityEditDialogTabsIndex;
    readonly selectedRecordIndex?: number;
    readonly updateActivity: (picassoIndex: number, newActivity: IActivity) => void;
    readonly onCloseEdit: () => void;
    readonly propertiesText: string;
    readonly actionsText: string;
    readonly triggersText: string;
    readonly participantsText: string;
    readonly transitionFromToText: string;
} & WorkflowEditorProps;

const ActivityEditDialogComponent: FunctionComponent<ActivityEditDialogComponentProps> = ({ hostKey, activity, isHidden, uiWorkflow, defaultTabIndex = 0, selectedRecordIndex = 0, updateActivity, onCloseEdit, propertiesText, actionsText, triggersText, participantsText, transitionFromToText }) => {
    const { actions, triggers, participants, picassoIndex } = activity;

    const [touched, setTouched] = useState(false);

    const handleUpdateActivity = (newActivity: IActivity) => {
        updateActivity(picassoIndex, newActivity);
        !touched && setTouched(true);
    };

    const updateActions = (newActions: readonly IAction[]) => {
        handleUpdateActivity({ ...activity, actions: newActions });
    };

    const updateTriggers = (newTriggers: readonly ITrigger[]) => {
        handleUpdateActivity({ ...activity, triggers: newTriggers });
    };

    const updateParticipants = (newParticipants: readonly IParticipant[]) => {
        handleUpdateActivity({ ...activity, participants: newParticipants });
    };

    const onCancelClick = () => {
        const { activities } = uiWorkflow.workflowModel;
        handleUpdateActivity(activities[picassoIndex - 1]); // minus 1, protože picasso má na prvním indexu sadu
    };

    const onSaveClick = async () => {
        const wfActivity = (uiWorkflow.picassoModel.components[picassoIndex] as UiActivity).activity;

        // Vyseparováno tady nahoře, aby pokud dojde k vyhození výjimky, tak se nové hodnoty neuložily do objektu aktivity
        const newActions = await createNewActions(activity.actions);
        const newTriggers = await createNewTriggers(activity.triggers);
        const newParticipants = createNewParticipants(uiWorkflow.workflow, activity.participants);

        wfActivity.name = activity.name;
        wfActivity.order = activity.order;
        wfActivity.inputTransType = activity.inputTransType;
        wfActivity.condition = activity.condition;
        wfActivity.runCondition = activity.runCondition;
        wfActivity.inheritParticipants = activity.inheritParticipants;
        wfActivity.killChildren = activity.killChildren;
        wfActivity.showInWfHistory = activity.showInWfHistory;
        wfActivity.description = activity.description;
        wfActivity.actions = newActions;
        wfActivity.triggers = newTriggers;
        wfActivity.participants = newParticipants;
    };

    return (
        <EditDialog width={1100} height={700} {...{ hostKey, isHidden, touched, onCloseEdit, onSaveClick, onCancelClick, setTouched }}>
            <Tabs {...{ defaultTabIndex }}>
                <Tab tabName={propertiesText}>
                    <ActivityPropertiesTab updateActivity={handleUpdateActivity} {...{ activity, hostKey }} />
                </Tab>
                <Tab tabName={transitionFromToText}>
                    <TransitionsFromToTab {...{ activityId: activity.id, uiWorkflow }} />
                </Tab>
                <Tab tabName={actionsText}>
                    <ActionsTab {...{ hostKey, actions, selectedRecordIndex, updateActions }} />
                </Tab>
                <Tab tabName={triggersText}>
                    <TriggersTab {...{ hostKey, triggers, selectedRecordIndex, updateTriggers }} />
                </Tab>
                <Tab tabName={participantsText}>
                    <ParticipantsTab selectedParticipants={participants} {...{ hostKey, updateParticipants }} />
                </Tab>
            </Tabs>
        </EditDialog>
    );
};

type ActivityEditDialogProps = {
    readonly picassoIndex: number;
} & Pick<ActivityEditDialogComponentProps, "onCloseEdit" | "hostKey" | "defaultTabIndex" | "selectedRecordIndex">;

type StateProps = Pick<ActivityEditDialogComponentProps, "activity" | "isHidden" | "propertiesText" | "actionsText" | "triggersText" | "participantsText" | "transitionFromToText">;

const mapStateToProps: MapStateToProps<StateProps, ActivityEditDialogProps, IAppState> = (state, { hostKey, picassoIndex }) => ({
    activity: getActivityByIndex(state, hostKey, picassoIndex),
    isHidden: isEditDialogHidden(state, hostKey),
    propertiesText: getPrimaryText(state, LOC_PROPERTIES) ?? "Vlastnosti",
    actionsText: getPrimaryText(state, LOC_ACTIONS) ?? "Akce",
    triggersText: getPrimaryText(state, LOC_TRIGGERS) ?? "Spouštěče",
    participantsText: getPrimaryText(state, LOC_PARTICIPANTS) ?? "Hráči",
    transitionFromToText: getPrimaryText(state, LOC_TRANSITION_FROM_TO) ?? "Propojení z/do",
});

type DispatchProps = Pick<ActivityEditDialogComponentProps, "updateActivity">;

const mapDispatchToProps: MapDispatchToProps<DispatchProps, ActivityEditDialogProps> = (dispatch, { hostKey }) => ({
    updateActivity: (index, newActivity) => dispatch(createUpdateActivityAction(hostKey, index, newActivity)),
});

const mergeProps: MergeProps<StateProps, DispatchProps, ActivityEditDialogProps, ActivityEditDialogComponentProps> = (
    stateProps,
    dispatchProps,
    ownProps,
) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    uiWorkflow: getUiWorkflowMutableObject(ownProps.hostKey),
});

export const ActivityEditDialog = connect(mapStateToProps, mapDispatchToProps, mergeProps)(ActivityEditDialogComponent);
